import React, { useState, useContext, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { gql, useQuery, useMutation } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import AddPartSidebar from "./AddPartSidebar";
import PartsCardView from "./PartsCardView";
import PartsListingView from "./PartsListingView";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import "../../starek.css";

const UI_STRINGS = {
    PAGE_TITLE: "Parts",
    SEARCH_PLACEHOLDER: "Search",
    VIEW_MODE_CARD: "Card View",
    VIEW_MODE_LIST: "List View",
};

const PART_MESSAGES = {
    DELETE_PARTS_BULK: (count) => `You are about to delete ${count} part(s).

This action cannot be undone.

Are you sure you wish to continue?`,
};

const Parts = () => {
    const starekContext = useContext(StarekContext);

    const GET_PARTS_LIST_FOR_PLANT = gql`
        query GetParts($plantId: Int!) {
            parts(where: { plant_id: { equals: $plantId } }) {
                id
                name
                description
                created_at
                updated_at
                created_by {
                    id
                    name
                }
                updated_by {
                    id
                    name
                }
            }
        }
    `;

    const DELETE_ONE_PART_MUTATION = gql`
        mutation DeleteOnePart($where: PartWhereUniqueInput!) {
            deleteOnePart(where: $where) {
                id
                deleted_at
            }
        }
    `;

    const DELETE_MANY_PARTS_MUTATION = gql`
        mutation DeleteManyParts($ids: [Int!]!) {
            deleteManyPart(where: { id: { in: $ids } }) {
                count
            }
        }
    `;

    const CREATE_ONE_PART_MUTATION = gql`
        mutation CreateOnePart($data: PartCreateInput!) {
            createOnePart(data: $data) {
                id
                name
                description
            }
        }
    `;

    const UPDATE_ONE_PART_MUTATION = gql`
        mutation UpdateOnePart(
            $data: PartUpdateInput!
            $where: PartWhereUniqueInput!
        ) {
            updateOnePart(data: $data, where: $where) {
                id
                name
                description
            }
        }
    `;

    const { data, loading, refetch } = useQuery(GET_PARTS_LIST_FOR_PLANT, {
        client: starekContext.engineClient,
        variables: { plantId: starekContext.lastUsedPlant.id },
    });

    const [deleteOnePart] = useMutation(DELETE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [deleteManyPart] = useMutation(DELETE_MANY_PARTS_MUTATION, {
        client: starekContext.engineClient,
    });
    const [createOnePart] = useMutation(CREATE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [updateOnePart] = useMutation(UPDATE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });

    const [showAddPartDrawer, setShowAddPartDrawer] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [viewMode, setViewMode] = useState("card");
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [toggleCleared, setToggleCleared] = useState(false);

    const items = data?.parts ?? [];

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleSavePart = async (partPayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ||
                starekContext?.user?.id ||
                starekContext?.me?.id;

            if (!currentUserId) {
                throw new Error("No current user in context");
            }

            const base = {
                name: partPayload.name,
                description: partPayload.description || "",
                plant: { connect: { id: Number(partPayload.plant_id) } },
            };

            if (!partPayload.id) {
                // Create
                const data = {
                    ...base,
                    created_by: { connect: { id: Number(currentUserId) } },
                    updated_by: { connect: { id: Number(currentUserId) } },
                };
                await createOnePart({ variables: { data } });
            } else {
                // Update
                const data = {
                    name: { set: partPayload.name },
                    description: { set: partPayload.description || "" },
                    updated_by: { connect: { id: Number(currentUserId) } },
                };
                await updateOnePart({
                    variables: { data, where: { id: partPayload.id } },
                });
            }

            await handleDrawerClose(true, false);
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleAdd = () => {
        setSelectedRows([]);
        setShowAddPartDrawer(true);
    };

    const handleEditSelected = () => {
        if (selectedRows.length !== 1) return;
        setShowAddPartDrawer(true);
    };

    const handleBulkDelete = () => {
        if (selectedRows.length === 0 || isDeleting) return;

        const isSingleSelected = selectedRows.length === 1;
        const title = isSingleSelected
            ? `Delete Part ${selectedRows[0].name}?`
            : `Delete ${selectedRows.length} Parts?`;

        openConfirm({
            title: title,
            message: PART_MESSAGES.DELETE_PARTS_BULK(selectedRows.length),
            confirmText: "Delete",
            cancelText: "Cancel",
            type: "danger",
            count: selectedRows.length,
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);

                const ids = selectedRows.map((r) => r.id);

                try {
                    await deleteManyPart({ variables: { ids } });
                    setDeleteDone(ids.length);
                } catch {
                    for (const id of ids) {
                        try {
                            await deleteOnePart({
                                variables: { where: { id } },
                            });
                            setDeleteDone((d) => d + 1);
                        } catch (err) {
                            starekContext.showError?.(err);
                            break;
                        }
                    }
                } finally {
                    setIsDeleting(false);
                    setSelectedRows([]);
                    setToggleCleared((p) => !p);
                    await refetch();
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    const handleDrawerClose = async (itemModified, itemDeleted) => {
        setShowAddPartDrawer(false);
        setSelectedRows([]);
        setToggleCleared((p) => !p);

        await new Promise((resolve) => setTimeout(resolve, 0));
        await refetch();
    };

    const handleDeletePart = async (part) => {
        if (!part?.id) return;

        openConfirm({
            title: `Delete Part ${part.name}?`,
            message: `You are about to delete part "${part.name}".\n\nThis action cannot be undone.\n\nAre you sure you wish to continue?`,
            confirmText: "Delete",
            cancelText: "Cancel",
            type: "danger",
            count: 1,
            onConfirm: async () => {
                try {
                    await deleteOnePart({
                        variables: { where: { id: part.id } },
                    });
                    await handleDrawerClose(false, true);
                } catch (error) {
                    starekContext.showError(error);
                } finally {
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    return loading ? (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
        >
            <div className="text-xs d-flex justify-content-center">
                <i className="fa fa-cube fa-10x text-success"></i>
            </div>
            <Spinner />
        </div>
    ) : (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{ singular: "Part", plural: "Parts" }}
                allowSearch={true}
                allowAdd={true}
                allowDelete={true}
                allowEdit={true}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                searchPlaceholder={UI_STRINGS.SEARCH_PLACEHOLDER}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => setViewMode(mode)}
                onDelete={handleBulkDelete}
                onEdit={handleEditSelected}
            >
                <EntityOperationBar.View
                    mode="card"
                    icon="fa-th"
                    tooltip={UI_STRINGS.VIEW_MODE_CARD}
                >
                    <PartsCardView
                        items={items}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa-list-ul"
                    tooltip={UI_STRINGS.VIEW_MODE_LIST}
                >
                    <PartsListingView
                        items={items}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                        toggleCleared={toggleCleared}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            {showAddPartDrawer && (
                <AddPartSidebar
                    show={true}
                    item={selectedRows.length === 1 ? selectedRows[0] : null}
                    plantId={starekContext.lastUsedPlant.id}
                    onDiscard={handleDrawerClose}
                    onSave={handleSavePart}
                    onDelete={handleDeletePart}
                />
            )}

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                message={confirmCfg.message}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
                count={confirmCfg.count}
                done={deleteDone}
            />
        </div>
    );
};

export { Parts };
